import { createResource, createSignal, For, Show, type JSX } from 'solid-js';
import { fetchStrainImages, submitStrainImage } from '@lib/supabase/reviews';
import { authStore } from '@stores/session';

// User-submitted strain imagery. URLs only (no hosting) — each submission
// starts out pending and only appears in the public listing after admin
// approval, enforced by the `strain_images_public_read_approved` RLS policy.
export default function StrainImagesSection(props: { strainId: string }): JSX.Element {
  const [images] = createResource(() => props.strainId, fetchStrainImages);
  const [composing, setComposing] = createSignal(false);
  const [url, setUrl] = createSignal('');
  const [caption, setCaption] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [success, setSuccess] = createSignal(false);

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await submitStrainImage(props.strainId, url(), caption());
      setUrl('');
      setCaption('');
      setComposing(false);
      setSuccess(true);
      // No refetch — RLS hides pending-status submissions from the
      // submitter, so a refetch would return the same data we already
      // have. The success message is the user-facing acknowledgement;
      // the new image appears in the public list once an admin
      // approves it (and any open tab refetches naturally on
      // navigation).
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="mt-8">
      <div class="flex items-center justify-between">
        <h2 class="font-display text-xl font-semibold">Images</h2>
        <Show when={authStore.user() && !composing()}>
          <button
            type="button"
            onClick={() => setComposing(true)}
            class="text-xs rounded-full border border-brand-hex px-3 py-1 hover:bg-brand-hex/40"
          >
            + Submit image
          </button>
        </Show>
      </div>

      <Show when={success()}>
        <p class="mt-2 text-xs text-brand-moss">
          Thanks &ndash; your image is queued for moderator review.
        </p>
      </Show>

      <Show when={composing()}>
        <form onSubmit={submit} class="mt-3 rounded-xl border border-brand-hex bg-white p-3 flex flex-col gap-2">
          <label class="text-xs">
            Image URL
            <input
              type="url" name="url"
              required
              value={url()}
              onInput={(e) => setUrl(e.currentTarget.value)}
              placeholder="https://…"
              class="mt-1 w-full rounded-md border border-brand-hex px-3 py-2 text-sm"
            />
          </label>
          <label class="text-xs">
            Caption (optional)
            <input
              type="text" name="text"
              value={caption()}
              onInput={(e) => setCaption(e.currentTarget.value)}
              class="mt-1 w-full rounded-md border border-brand-hex px-3 py-2 text-sm"
            />
          </label>
          <div class="flex gap-2">
            <button
              type="submit"
              disabled={busy() || !url().trim()}
              class="rounded-full bg-brand-moss text-brand-cream text-xs font-semibold px-4 py-2 disabled:opacity-50"
            >
              {busy() ? 'Submitting…' : 'Submit for review'}
            </button>
            <button
              type="button"
              onClick={() => setComposing(false)}
              class="rounded-full border border-brand-hex text-xs px-4 py-2"
            >
              Cancel
            </button>
          </div>
          <Show when={error()}>
            <p class="text-xs text-red-700">{error()}</p>
          </Show>
        </form>
      </Show>

      <div class="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
        <For
          each={images() ?? []}
          fallback={
            <p class="col-span-full text-xs text-brand-green/60">No images yet.</p>
          }
        >
          {(img) => (
            <figure class="rounded-lg overflow-hidden border border-brand-hex bg-white">
              <img src={img.url} alt={img.caption ?? ''} class="w-full h-40 object-cover" />
              <Show when={img.caption}>
                <figcaption class="px-2 py-1 text-[11px] text-brand-green/80">
                  {img.caption}
                </figcaption>
              </Show>
            </figure>
          )}
        </For>
      </div>
    </section>
  );
}
